'use client';

import Edit from '@/_assets/icon-components/Edit';
import TrashIcon from '@/_assets/icon-components/Trash';
import BottomSheet from '@/_components/Sheet';
import { getCheckPointDeliveryZones } from '@/_service/zone.service';
import {
  activatePoint,
  onOpenMap,
  openPoint,
  removePoint,
  setDeliveryZone,
} from '@/_store/common/common.slice';
import { IPoint } from '@/_store/common/types';
import { IRedux } from '@/_store/types';
import { toaster } from '@/components/ui/toaster';
import { Box, Button, Flex, Text } from '@chakra-ui/react';
import { MapPin } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { FC, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import BranchSelectSheet from '../../BranchSelectSheet';
import ConfirmModal from '../../ConfirmModal';
import { IProps } from '../_types';
import { useActiveBrand } from '@/_hooks/useActiveBrand';

const Mobile: FC<IProps> = ({ isOpen, onClose }) => {
  const shipperId = useActiveBrand();
  const { points } = useSelector((state: IRedux) => state.common);
  const deliveryZone = useSelector(
    (state: IRedux) => state.common.deliveryZones?.[shipperId] ?? null,
  );
  const { data } = useSelector((state: IRedux) => state.settings);

  const t = useTranslations();
  const dispatch = useDispatch();
  const [isDelete, setIsDelete] = useState<string>('');
  const [isBottomSheet, setIsBottomSheet] = useState(false);

  const isChooseDeliveryBranch = data?.choose_delivery_branch;

  const handleClick = () => {
    dispatch(onOpenMap());
    onClose();
  };

  const handleTariffGeozone = (lat: any, lon: any, point: IPoint) => {
    getCheckPointDeliveryZones({ lat, lon }, shipperId).then((data) => {
      if (data?.data?.all_count > 0 && !data?.data?.result?.length) {
        toaster.create({
          title: t('address_not_in_service_area'),
          type: 'error',
        });
      } else {
        const currentZone = data?.data?.result?.[0];
        if (currentZone?.id && shipperId) {
          dispatch(
            setDeliveryZone({
              shipper_id: shipperId,
              zone: {
                id: currentZone?.id,
                min_order_amount: currentZone?.min_order_amount,
              },
            }),
          );
        } else if (deliveryZone?.id && shipperId) {
          dispatch(setDeliveryZone({ shipper_id: shipperId, zone: null }));
        }
        if (isChooseDeliveryBranch) setIsBottomSheet(true);
        dispatch(activatePoint(point.id));
      }
    });
  };

  const handleSelected = (point: IPoint) => {
    if (point?.location && point?.location?.[0] && point?.location?.[1]) {
      handleTariffGeozone(point?.location[0], point?.location[1], point);
    }
  };

  if (points?.length > 0)
    return (
      <>
        <BottomSheet isOpen={isOpen} onClose={onClose}>
          <Flex direction='column' h='100%'>
            <Text as='p' color={'text'} px={4} fontSize='xl' fontWeight='semibold'>
              {t('addresses')}
            </Text>

            <Box>
              {points?.map((point, index) => (
                <Box key={index} borderBottom='1px solid #E5E5E5'>
                  <Flex align='center' justifyContent='space-between'>
                    <Flex
                      flex={1}
                      align='center'
                      gap={2}
                      p={4}
                      onClick={() => {
                        handleSelected(point);
                        onClose();
                      }}
                    >
                      <Box w='24px' h='24px'>
                        <MapPin strokeWidth={1.5} />
                      </Box>

                      <Box>
                        <Text color={'text'} fontWeight='medium' fontSize='md'>
                          {point.name || point.address}
                        </Text>
                        <Text
                          wordBreak='break-all'
                          color={'text.muted'}
                          fontSize='md'
                          lineClamp={1}
                        >
                          {point.name ? point.address : point.city}
                        </Text>
                      </Box>
                    </Flex>

                    <Box display='flex' mr='3'>
                      {point.name && (
                        <Button
                          p={0}
                          bg='transparent'
                          onClick={() => dispatch(openPoint(point))}
                        >
                          <Edit />
                        </Button>
                      )}
                      <Button
                        p={0}
                        variant='ghost'
                        onClick={() => {
                          setIsDelete(point.id);
                        }}
                      >
                        <TrashIcon color='red' />
                      </Button>
                    </Box>
                  </Flex>
                </Box>
              ))}
            </Box>

            <Box px={4} mb={4} mt='auto'>
              <Button
                onClick={handleClick}
                w={'100%'}
                size='xl'
                mt='4'
                rounded={16}
                h='56px'
                bg='primary'
                color='primary.text'
                _hover={{
                  bg: 'primary.hover',
                }}
              >
                {t('add_new_address')}
              </Button>
            </Box>
          </Flex>
        </BottomSheet>

        <BranchSelectSheet
          isOpen={isBottomSheet}
          onClose={() => setIsBottomSheet(false)}
        />

        <ConfirmModal
          isWarning={true}
          isOpen={Boolean(isDelete)}
          onConfirm={() => {
            toaster.create({
              title: t('address_successfully_deleted'),
              type: 'success',
            });
            dispatch(removePoint(isDelete));
            setIsDelete('');
          }}
          title={t('remove_address')}
          onClose={() => setIsDelete('')}
          description={''}
        />
      </>
    );

  return null;
};

export default Mobile;
